import React, { useMemo } from 'react';
import { AssetDiameterBreakDown, AssetMaterialBreakDown, AssetYearLaidBreakDown } from '@Types';
import { useGlobalization } from '@Translations';
import Highcharts from 'highcharts';
import { Box, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import { selectAssetBreakDownChartStore } from '@Selectors';
import { useUnits } from '@innovyze/stylovyze';
import { diameterSlicesForDisplay } from '@Components/deteriorationModel/utils/unitDisplay';
import { yearSeriesForDisplay } from '@Components/deteriorationModel/utils/yearBreakdown';
import CloseIcon from '@mui/icons-material/Close';
import { Warning } from '@mui/icons-material';
import { baseChartOptions } from '@Components/deteriorationModel/utils/chartDefaults';

export const MaterialAssetPieChart = (props: { data: AssetMaterialBreakDown; animate?: boolean }) => {
	const { t } = useGlobalization();
	const { data: rawData } = props;
	// Derived during render rather than set in an effect, so the chart's first draw
	// already has its data. Starting from an empty series and filling it after mount
	// drew the chart twice, which showed as a jump when it is drawn without animation.
	// Convert rawData object to array format [['CP', 392], ['VCP', 50]]
	const data = useMemo<(string | number)[][]>(
		() => Object.entries(rawData ?? {}).map(([key, value]) => [key, value.count || 0]),
		[rawData],
	);

	const chartOptions: Highcharts.Options = {
		...baseChartOptions,
		chart: {
			animation: props.animate ?? true,
			type: 'pie',
			height: 260,
			zooming: {
				type: 'xy',
			},
			panning: {
				enabled: true,
				type: 'xy',
			},
			panKey: 'shift',
		},
		title: {
			text: t('Material'),
			style: {
				fontSize: '0.8125rem',
				fontWeight: '600',
			},
		},
		tooltip: {
			pointFormat: '<span style="color:{point.color}">●</span> : {point.percentage:.1f}%<br />{point.y} Assets',
		},
		plotOptions: {
			series: { animation: props.animate ?? true },
			pie: {
				allowPointSelect: true,
				cursor: 'pointer',
				startAngle: -90,
				endAngle: 90,
				dataLabels: [
					{
						enabled: true,
						distance: 20,
					},
					{
						enabled: false,
						distance: -40,
						format: '{point.percentage:.1f}%',
						style: {
							fontSize: '1.2em',
							textOutline: 'none',
							opacity: 0.7,
						},
					},
				],
			},
		},
		series: [
			{
				name: t('Material'),
				data: data,
				type: 'pie',
			},
		],
	};

	return (
		<HighchartsReact
			highcharts={Highcharts}
			options={chartOptions}
			containerProps={{ style: { height: '260px', width: '100%' } }}
		/>
	);
};

const diameterSortValue = (key: string): number => {
	const value = key.trim() === '' ? Number.NaN : Number(key);
	return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
};

/** Numeric diameters in ascending order; anything non-numeric (e.g. unknown) last, by name. */
const compareDiameters = (a: string, b: string): number =>
	diameterSortValue(a) - diameterSortValue(b) || a.localeCompare(b);

export const DiameterAssetPieChart = (props: {
	data: AssetDiameterBreakDown;
	/** Every diameter across all grades, so each card has the same slices. */
	allDiameters?: string[];
	animate?: boolean;
}) => {
	const { t } = useGlobalization();
	const { getSystemValueFormatted } = useUnits();
	const { data: rawData, allDiameters } = props;

	// The server buckets by millimetre value; label each slice in the tenant's
	// display unit, with the unit shown. This is the same call the results table's
	// Diameter column makes, so a slice and a cell for one pipe match. Derived during
	// render so the first draw already has its data, and the labels follow a
	// unit-system change as well as new data.
	//
	// Highcharts updates a pie in place by position when the card changes. Material
	// gets the same materials on every card, so its slices just resize. Diameters
	// differ between grades, so slices were reassigned to other diameters and the
	// chart no longer matched the same card drawn fresh. Every card now gets the full
	// diameter list in a fixed numeric order, with 0 for any this grade lacks.
	const data = useMemo(() => {
		if (!rawData) return [];
		const keys = Array.from(new Set([...(allDiameters ?? []), ...Object.keys(rawData)])).sort(compareDiameters);
		return diameterSlicesForDisplay(rawData, mm => getSystemValueFormatted(`${mm}mm`, mm, 1) ?? mm, keys);
	}, [rawData, allDiameters, getSystemValueFormatted]);

	const chartOptions: Highcharts.Options = {
		...baseChartOptions,
		chart: {
			animation: props.animate ?? true,
			type: 'pie',
			height: 260,
			zooming: {
				type: 'xy',
			},
			panning: {
				enabled: true,
				type: 'xy',
			},
			panKey: 'shift',
		},
		title: {
			text: t('Diameter'),
			style: {
				fontSize: '0.8125rem',
				fontWeight: '600',
			},
		},
		tooltip: {
			pointFormat: '<span style="color:{point.color}">●</span> : {point.percentage:.1f}%<br />{point.y} Assets',
		},
		plotOptions: {
			series: { animation: props.animate ?? true },
			pie: {
				allowPointSelect: true,
				cursor: 'pointer',
				startAngle: -90,
				endAngle: 90,
				dataLabels: [
					{
						enabled: true,
						distance: 20,
						// Diameters this grade lacks are 0-count slices; label only real ones.
						filter: { property: 'y', operator: '>', value: 0 },
					},
					{
						enabled: false,
						distance: -40,
						format: '{point.percentage:.1f}%',
						style: {
							fontSize: '1.2em',
							textOutline: 'none',
							opacity: 0.7,
						},
					},
				],
			},
		},
		series: [
			{
				name: t('Diameter'),
				data: data,
				type: 'pie',
			},
		],
	};

	return (
		<HighchartsReact
			highcharts={Highcharts}
			options={chartOptions}
			containerProps={{ style: { height: '260px', width: '100%' } }}
		/>
	);
};

export const YearLaidLineChart = (props: { data: AssetYearLaidBreakDown; animate?: boolean }) => {
	const { t } = useGlobalization();
	const { data: rawData } = props;

	// Plotted on a numeric axis so years are spaced to scale; see yearSeriesForDisplay.
	const { points, unknownCount } = useMemo(
		() => (rawData ? yearSeriesForDisplay(rawData) : { points: [], unknownCount: 0 }),
		[rawData],
	);
	// A dot marks a year that has assets. Years filled in at zero only hold the line
	// at zero across a gap, so they carry no marker.
	const seriesData = useMemo(
		() =>
			points.map(({ year, count, filled }): [number, number] | Highcharts.PointOptionsObject =>
				filled ? { x: year, y: count, marker: { enabled: false } } : [year, count],
			),
		[points],
	);
	// "Installation Year" rather than "Year Laid": the latter is Sanitary Sewer
	// terminology, and this matches the results table's column for every system type.
	const installationYearLabel = t('Installation Year');

	const chartOptions: Highcharts.Options = {
		...baseChartOptions,
		chart: {
			animation: props.animate ?? true,
			type: 'line',
			height: 260,
		},
		title: {
			text: installationYearLabel,
			style: {
				fontSize: '0.8125rem',
				fontWeight: '600',
			},
		},
		// Assets with no valid installation year have no place on a year axis. Say how
		// many there are rather than dropping them silently. Always an object, with
		// empty text when there is nothing to report: the chart stays mounted across
		// grade cards, and Highcharts reads an undefined subtitle on update as "no
		// change", which would leave the previous grade's count on screen.
		// Deliberately not using i18next's `count` option: plural keys cannot be
		// generated or resolved for the underscore locale codes (de_DE, fr_FR, ja_JA),
		// so a pluralised string would always show in English. This wording reads
		// correctly for any number and translates as a plain key.
		subtitle: {
			text:
				unknownCount > 0
					? t('Assets with no valid installation year (not shown): {{total}}', { total: unknownCount })
					: '',
			style: { fontSize: '0.75rem' },
		},
		xAxis: {
			title: {
				text: t('Year'),
			},
			allowDecimals: false,
		},
		tooltip: {
			pointFormat: '{point.y} Assets',
		},
		yAxis: {
			title: {
				text: t('Count'),
			},
		},
		plotOptions: {
			series: { animation: props.animate ?? true },
			line: {
				dataLabels: {
					enabled: false,
				},
				enableMouseTracking: true,
			},
		},
		series: [
			{
				name: installationYearLabel,
				data: seriesData,
				type: 'line',
				// Forced on: left to its default, Highcharts hides every marker once points
				// are closer than about 8px, which a year axis from 1900 easily triggers.
				marker: { enabled: true, radius: 4 },
			},
		],
	};

	return (
		<HighchartsReact
			highcharts={Highcharts}
			options={chartOptions}
			containerProps={{ style: { height: '260px', width: '100%' } }}
		/>
	);
};

/**
 * `animate: false` draws the charts in place, with no opening or update animation. Used
 * when the breakdown is restored on returning to the page, and when switching cards.
 */
export const AssetBreakDownChart = (props: {
	type: string;
	topText: string;
	onHide: () => void;
	animate?: boolean;
}) => {
	const { t } = useGlobalization();
	const { type, topText, onHide, animate = true } = props;
	const chartData = selectAssetBreakDownChartStore();

	// Check if data exists for the selected type
	const selectedTypeData = chartData?.asset_breakdown?.[type];
	const allDiameters = useMemo(
		() =>
			Array.from(
				new Set(Object.values(chartData?.asset_breakdown ?? {}).flatMap(grade => Object.keys(grade?.diameter ?? {}))),
			),
		[chartData?.asset_breakdown],
	);

	return (
		<Box sx={{ backgroundColor: 'white', padding: 1.5 }}>
			<Grid container spacing={2}>
				<Grid item xs={10}>
					<Typography variant="h6">{`${t('Asset Breakdown')}: ${topText}`}</Typography>
					<Typography variant="body2" sx={{ mb: 2 }}>
						{t('Click a card above to view  different failure grade')}
					</Typography>
				</Grid>
				<Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
					<IconButton onClick={onHide} size="small" sx={{ color: 'text.secondary' }} title={t('Hide')}>
						<CloseIcon />
					</IconButton>
				</Grid>
			</Grid>
			{!chartData || !selectedTypeData ? (
				<Grid container spacing={2}>
					<Grid item xs={4}>
						<Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 1 }} />
					</Grid>
					<Grid item xs={4}>
						<Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 1 }} />
					</Grid>
					<Grid item xs={4}>
						<Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 1 }} />
					</Grid>
				</Grid>
			) : (
				<>
					{selectedTypeData && (selectedTypeData.assets_with_empty_length ?? 0) > 0 && (
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Box
									sx={{
										display: 'flex',
										width: '100%',
										overflow: 'hidden',
										border: '1px solid #EDA641',
									}}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											backgroundColor: '#EDA641',
											padding: '8px',
										}}>
										<Warning sx={{ color: '#FFFEFE', fontSize: '20px' }} />
									</Box>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											backgroundColor: '#FBEDD4',
											padding: '8px 12px',
											flex: 1,
										}}>
										<Typography sx={{ color: '#746E67', fontSize: '0.875rem', fontWeight: 'bold' }}>
											{t('{{count}} assets with empty length were excluded from this breakdown.', {
												count: selectedTypeData.assets_with_empty_length,
											})}
										</Typography>
									</Box>
								</Box>
							</Grid>
						</Grid>
					)}
					<Grid container spacing={2}>
						<Grid item xs={4}>
							<MaterialAssetPieChart data={selectedTypeData.material} animate={animate} />
						</Grid>
						<Grid item xs={4}>
							<DiameterAssetPieChart data={selectedTypeData.diameter} allDiameters={allDiameters} animate={animate} />
						</Grid>
						<Grid item xs={4}>
							<YearLaidLineChart data={selectedTypeData.year_laid} animate={animate} />
						</Grid>
					</Grid>
				</>
			)}
		</Box>
	);
};
